import { useState } from 'react';
import { Check, ChevronDown, Plus, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface WorkspaceSwitcherProps {
    collapsed?: boolean;
}

export function WorkspaceSwitcher({ collapsed = false }: WorkspaceSwitcherProps) {
    const navigate = useNavigate();
    const {
        currentWorkspace,
        workspaces,
        switchWorkspace,
        loading,
    } = useWorkspace();
    const [switching, setSwitching] = useState(false);

    const handleSwitch = async (workspaceId: string) => {
        if (workspaceId === currentWorkspace?.id) return;

        setSwitching(true);
        try {
            await switchWorkspace(workspaceId);
        } finally {
            setSwitching(false);
        }
    };

    const handleCreateNew = () => {
        navigate('/app/workspace/new');
    };

    // ── Loading skeleton ──
    if (loading && !currentWorkspace) {
        return (
            <div className={cn(
                "flex items-center rounded-xl",
                collapsed ? "justify-center py-2" : "gap-2.5 px-3 py-2"
            )}>
                <div className="h-[18px] w-[18px] rounded bg-muted animate-pulse flex-shrink-0" />
                {!collapsed && (
                    <div className="flex-1">
                        <div className="h-3.5 w-20 bg-muted rounded animate-pulse" />
                    </div>
                )}
            </div>
        );
    }

    // ── No workspace — create button ──
    if (!currentWorkspace) {
        return (
            <Button
                variant="ghost"
                onClick={handleCreateNew}
                className={cn(
                    "w-full rounded-xl h-auto py-2 transition-all duration-200",
                    "text-muted-foreground hover:bg-white/30 dark:hover:bg-white/10 hover:text-foreground",
                    collapsed ? "justify-center px-0" : "justify-start gap-2.5 px-3"
                )}
            >
                <Plus className="h-[18px] w-[18px] flex-shrink-0" />
                {!collapsed && <span className="text-[13px] font-medium">Create Workspace</span>}
            </Button>
        );
    }

    // ── Collapsed — icon-only trigger ──
    if (collapsed) {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={cn(
                            "w-full flex items-center justify-center rounded-xl py-2 transition-all duration-200",
                            "text-muted-foreground hover:bg-white/30 dark:hover:bg-white/10 hover:text-foreground",
                        )}
                        disabled={switching}
                    >
                        <Building2 className="h-[18px] w-[18px]" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="right" className="w-60">
                    <div className="px-3 py-2 text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                        Workspaces
                    </div>
                    {workspaces.map((workspace) => (
                        <DropdownMenuItem
                            key={workspace.id}
                            onClick={() => handleSwitch(workspace.id)}
                            className="flex items-center gap-2.5 px-3 py-2"
                        >
                            <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="flex-1 truncate text-[13px]">{workspace.name}</span>
                            {workspace.id === currentWorkspace.id && (
                                <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                            )}
                        </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleCreateNew} className="gap-2.5 px-3 py-2">
                        <Plus className="h-4 w-4" />
                        <span className="text-[13px]">Create Workspace</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    // ── Expanded — full trigger with name ──
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-200",
                        "text-foreground hover:bg-white/30 dark:hover:bg-white/10",
                    )}
                    disabled={switching}
                >
                    <Building2 className="h-[18px] w-[18px] text-primary flex-shrink-0" />
                    <div className="flex flex-col items-start min-w-0 flex-1">
                        <span className="text-[13px] font-medium truncate w-full text-left">
                            {currentWorkspace.name}
                        </span>
                        {currentWorkspace.pricingModel && (
                            <span className="text-[10px] text-muted-foreground leading-tight">
                                {currentWorkspace.pricingModel.displayName}
                            </span>
                        )}
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
                <div className="px-3 py-2 text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    Workspaces
                </div>
                {workspaces.map((workspace) => (
                    <DropdownMenuItem
                        key={workspace.id}
                        onClick={() => handleSwitch(workspace.id)}
                        className="flex items-center gap-2.5 px-3 py-2"
                    >
                        <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="truncate text-[13px]">{workspace.name}</div>
                            {workspace.pricingModel && (
                                <div className="text-[10px] text-muted-foreground">
                                    {workspace.pricingModel.displayName}
                                </div>
                            )}
                        </div>
                        {workspace.id === currentWorkspace.id && (
                            <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                        )}
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleCreateNew} className="gap-2.5 px-3 py-2">
                    <Plus className="h-4 w-4" />
                    <span className="text-[13px]">Create Workspace</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
